import { useCallback, useEffect, useRef, useState } from 'react';

interface UgcSlide {
  video: string;
  handle: string;
  cta: string;
  accent: string;
}

/** Real UGC ad clips served from the Coinis CDN. */
const UGC: UgcSlide[] = [
  {
    video: 'https://cdn.coinis.com/ugc-montenegro-submarine.mp4',
    handle: '@submarine',
    cta: 'Book a dive',
    accent: 'linear-gradient(135deg, #fbcfa6, #c2452d)'
  },
  {
    video: 'https://cdn.coinis.com/ugc-astercook-knife-set.mp4',
    handle: '@astercook',
    cta: 'Order now',
    accent: 'linear-gradient(135deg, #ffd0b8, #c2452d)'
  },
  {
    video: 'https://cdn.coinis.com/ugc-montenegro-submarine2.mp4',
    handle: '@submarine',
    cta: 'Book a dive',
    accent: 'linear-gradient(135deg, #ffd4b3, #a03520)'
  },
  {
    video: 'https://cdn.coinis.com/ugc-KiNGKANG.mp4',
    handle: '@kingkang',
    cta: 'Shop now',
    accent: 'linear-gradient(135deg, #f6c094, #d4763a)'
  },
  {
    video: 'https://cdn.coinis.com/ugc-kids-tracker.mp4',
    handle: '@kidstracker',
    cta: 'Shop now',
    accent: 'linear-gradient(135deg, #ffe4cf, #d4763a)'
  },
  {
    video: 'https://cdn.coinis.com/ugc-dino.mp4',
    handle: '@dino',
    cta: 'Get yours',
    accent: 'linear-gradient(135deg, #f4d8b9, #a03520)'
  }
];

const FRAME_SHADOW =
  '0 34px 80px -30px rgba(0,0,0,.75), 0 0 0 1px rgba(255,255,255,.09), inset 0 0 0 5px rgba(0,0,0,.3)';
const SCENE_VIGNETTE = 'linear-gradient(to top, rgba(0,0,0,.6), transparent 34%, transparent 60%, rgba(0,0,0,.32))';
const CTA_DOT = 'conic-gradient(from 210deg, #ff6a40, #f9ae3b, #ff6a40)';
/** if a clip hasn't started playing within this window, skip it (covers a failed/blocked load) */
const STALL_GUARD_MS = 6000;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const avatarLetter = (handle: string): string => handle.replace('@', '').charAt(0).toUpperCase();

interface SlideOverlayProps {
  slide: UgcSlide;
}

/** Story-style overlay: handle + Sponsored (top), CTA pill + Made-with-Coinis (bottom). */
const SlideOverlay = ({ slide }: SlideOverlayProps): JSX.Element => (
  <>
    <span className="absolute inset-0" style={{ background: SCENE_VIGNETTE }} />

    {/* Top — avatar + handle + Sponsored */}
    <div className="absolute left-[14px] right-[14px] top-[30px] z-[6] flex items-center gap-2">
      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-[1.5px] border-white/85 bg-white/20 text-[12px] font-bold text-white backdrop-blur-[4px]">
        {avatarLetter(slide.handle)}
      </span>
      <span className="text-[12.5px] font-bold text-white" style={{ textShadow: '0 1px 6px rgba(0,0,0,.5)' }}>
        {slide.handle}
      </span>
      <span className="ml-auto rounded-full border border-white/70 px-[9px] py-[3px] text-[10px] font-bold tracking-[.03em] text-white">
        Sponsored
      </span>
    </div>

    {/* Bottom — CTA pill + Made-with-Coinis badge */}
    <div className="absolute inset-x-[14px] bottom-[16px] z-[6] flex items-center justify-between gap-2">
      <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-3.5 py-2 text-[12px] font-bold text-[#012d6c] shadow-[0_4px_14px_rgba(0,0,0,.35)]">
        {slide.cta}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>
      <span className="inline-flex shrink-0 items-center gap-[5px] rounded-full border border-white/20 bg-black/50 px-2 py-1 text-[9.5px] font-bold tracking-[.02em] text-white backdrop-blur-[6px]">
        <span className="h-[7px] w-[7px] rounded-full" style={{ background: CTA_DOT }} />
        Made with Coinis
      </span>
    </div>
  </>
);

/**
 * Rotating UGC video player. Plays each clip muted/inline and advances when it ends
 * (with a stall-guard so a failed/blocked clip is skipped). Progress bars track playback.
 * Honors prefers-reduced-motion: renders a single static slide, no autoplay/rotation.
 */
export const UgcPlayer = (): JSX.Element => {
  const reduced = prefersReducedMotion();
  const N = UGC.length;
  const [cur, setCur] = useState(0);
  const [progress, setProgress] = useState(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const advance = useCallback(() => setCur((c) => (c + 1) % N), [N]);
  const goPrev = useCallback(() => setCur((c) => (c - 1 + N) % N), [N]);

  useEffect(() => {
    if (reduced) {
      return;
    }

    setProgress(0);
    let started = false;
    const active = videoRefs.current[cur];

    videoRefs.current.forEach((v, i) => {
      if (v && i !== cur) {
        v.pause();
      }
    });

    if (active) {
      active.currentTime = 0;
      void active.play().catch(() => undefined); // muted autoplay; ignore rejection
    }

    const onTime = (): void => {
      started = true;
      if (active && active.duration > 0) {
        setProgress(active.currentTime / active.duration);
      }
    };

    const onEnd = (): void => advance();
    const onError = (): void => advance();

    active?.addEventListener('timeupdate', onTime);
    active?.addEventListener('ended', onEnd);
    active?.addEventListener('error', onError);

    // skip ahead if the clip never starts (failed/blocked load)
    const guard = window.setTimeout(() => {
      if (!started) {
        advance();
      }
    }, STALL_GUARD_MS);

    return () => {
      window.clearTimeout(guard);
      active?.removeEventListener('timeupdate', onTime);
      active?.removeEventListener('ended', onEnd);
      active?.removeEventListener('error', onError);
    };
  }, [cur, reduced, advance]);

  return (
    <div
      className="relative aspect-[9/16] w-[288px] self-center overflow-hidden rounded-[30px]"
      style={{ background: '#0b0d12', boxShadow: FRAME_SHADOW }}
    >
      {/* Progress bars */}
      <div className="absolute left-[13px] right-[13px] top-[13px] z-[7] flex gap-[3px]">
        {UGC.map((_, i) => {
          let width: string | number = 0;
          if (reduced) {
            width = i === cur ? '100%' : 0;
          } else if (i < cur) {
            width = '100%';
          } else if (i === cur) {
            width = `${Math.round(progress * 100)}%`;
          }

          return (
            <i
              key={i}
              className="h-[2.5px] flex-1 overflow-hidden rounded-[2px]"
              style={{ background: 'rgba(255,255,255,.3)' }}
            >
              <span
                className="block h-full rounded-[2px] bg-white transition-[width] duration-200 ease-linear"
                style={{ width }}
              />
            </i>
          );
        })}
      </div>

      {/* Slides */}
      {UGC.map((slide, i) => {
        const on = i === cur;
        return (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-[550ms] ease-[cubic-bezier(.16,1,.3,1)]"
            style={{ opacity: on ? 1 : 0, background: slide.accent }}
            aria-hidden={!on}
          >
            {!reduced && (
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                src={slide.video}
                muted
                playsInline
                preload="none"
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <SlideOverlay slide={slide} />
          </div>
        );
      })}

      {/* Tap zones — left half = previous, right half = next (story-style) */}
      <button
        type="button"
        aria-label="Previous video"
        onClick={goPrev}
        className="absolute inset-y-0 left-0 z-[8] w-1/2 cursor-pointer bg-transparent"
      />
      <button
        type="button"
        aria-label="Next video"
        onClick={advance}
        className="absolute inset-y-0 right-0 z-[8] w-1/2 cursor-pointer bg-transparent"
      />
    </div>
  );
};
